import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../UI/Buttons';
import { Text } from '../../UI/Typography';
import { Modal, Overlay } from '../../UI/Containers';
import wellknown from 'wellknown'; 

// Fix the icon paths issue in React with Leaflet (copy from Siteregisterform or centralize)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ModalHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const MapContainer = styled.div`
  height: 500px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LocationMapModal = ({ isOpen, onClose, locationData, sites }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const polygonLayerRef = useRef(null); 

  useEffect(() => {
    let map;

    // Only run map logic if the modal is open and the container ref exists
    if (isOpen && mapRef.current && locationData?.lat && locationData?.lng) {
      console.log('[MapModal Effect] Running for OPEN. locationData:', locationData);
      const { lat, lng } = locationData;
      const coordinates = [parseFloat(lat), parseFloat(lng)];

      // Initialize map ONLY if it doesn't exist yet
      if (!mapInstanceRef.current) {
        console.log('[MapModal Effect] Initializing map.');
        map = L.map(mapRef.current).setView(coordinates, 15); 
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(map);
        mapInstanceRef.current = map;

        // Add marker
        const marker = L.marker(coordinates).addTo(map);
        if (locationData.label) {
          marker.bindPopup(locationData.label).openPopup();
        }
        markerRef.current = marker;
      } 
      // If map ALREADY exists, update view and marker
      else {
        console.log('[MapModal Effect] Updating existing map view/marker.');
        map = mapInstanceRef.current;
        map.setView(coordinates, 15);

        if (markerRef.current) {
          markerRef.current.setLatLng(coordinates);
          if (locationData.label) {
            markerRef.current.bindPopup(locationData.label).openPopup();
          } else {
            markerRef.current.unbindPopup();
          }
        } else {
            const marker = L.marker(coordinates).addTo(map);
            if (locationData.label) {
                marker.bindPopup(locationData.label).openPopup();
            }
            markerRef.current = marker;
        }
      }

      // --- Polygon Logic (Runs AFTER map init/update) ---
      console.log('[MapModal Effect] Starting polygon update/add.');
      if (polygonLayerRef.current) {
        console.log('[MapModal Effect] Removing previous polygon layer.');
        polygonLayerRef.current.remove();
        polygonLayerRef.current = null;
      }

      let targetSite = null;
      const targetSiteName = locationData.siteName; 
      console.log(`[MapModal Effect] Looking for site name: "${targetSiteName}"`);

      if (targetSiteName && sites && sites.length > 0) {
        targetSite = sites.find(site => site.name === targetSiteName);
        console.log('[MapModal Effect] Found targetSite:', targetSite);
      } else {
        console.log('[MapModal Effect] No targetSiteName or sites array provided/empty.');
      }
      
      if (targetSite && targetSite.boundary) {
        console.log(`[MapModal Effect] Found boundary for ${targetSite.name}:`, targetSite.boundary);
        try {
          const geoJsonBoundary = wellknown.parse(targetSite.boundary);
          console.log('[MapModal Effect] Parsed GeoJSON:', geoJsonBoundary);

          if (geoJsonBoundary && geoJsonBoundary.type === 'Polygon') {
            console.log(`[MapModal Effect] Adding Polygon layer for site: ${targetSite.name}`);
            if (map) { 
              polygonLayerRef.current = L.geoJSON(geoJsonBoundary, {
                style: { color: '#f4511e', weight: 2, opacity: 0.8, fillOpacity: 0.1 }
              }).addTo(map);
              console.log('[MapModal Effect] Polygon layer added.');
            } else {
               console.error('[MapModal Effect] Map instance is not available to add polygon layer.');
            }
          } else {
            console.warn(`[MapModal Effect] Parsed boundary data for site ${targetSite.name} is not a valid Polygon. Type: ${geoJsonBoundary?.type}`);
          }
        } catch (e) {
          console.error(`[MapModal Effect] Error parsing WKT for site ${targetSite.name}:`, e);
        }
      } else if (targetSite) {
           console.warn(`[MapModal Effect] Target site ${targetSite.name} found, but has no boundary property.`);
      } else if (targetSiteName) {
          console.warn(`[MapModal Effect] Site named "${targetSiteName}" not found in the sites array.`);
      } else {
          console.log("[MapModal Effect] No specific site name provided, not displaying boundary.");
      }

      // Invalidate size after updates/initialization
      if (map) {
        setTimeout(() => map.invalidateSize(), 100); 
      }
    } 
    // Cleanup when the modal is closed
    else if (!isOpen && mapInstanceRef.current) {
        console.log('[MapModal Effect] Cleaning up map instance.');
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
        polygonLayerRef.current = null;
    }

  }, [isOpen, locationData, sites]);

  if (!isOpen || !locationData) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal size="large" maxWidth="900px">
        <ModalHeader>
          <Text size="lg" weight="semibold" style={{ margin: '0 0 4px 0' }}>
            Location: {locationData.label || 'Coordinates'}
          </Text>
          <Text size="sm" color="#6b7280" style={{ margin: '0 0 4px 0' }}>
            Lat: {locationData.lat}, Lng: {locationData.lng}
          </Text>
          {locationData.siteName && (
            <Text size="xs" color="#6b7280" style={{ margin: 0 }}>
              Site: {locationData.siteName}
            </Text>
          )}
        </ModalHeader>
        
        <MapContainer ref={mapRef} />
        
        <ButtonGroup>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default LocationMapModal;
